import type React from 'react';
import { Text, Badge, Group, Title, ActionIcon, Menu } from '@mantine/core';
import { IconArchive, IconArchiveOff, IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import { EditableText } from '@ayocore/platform/client/components/EditableText';
import type { LeadExtended } from '../LeadViewController';

interface LeadHeaderProps {
  lead: LeadExtended;
  canEdit?: boolean;
  canDelete?: boolean;
  permissionsLoading?: boolean;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onTitleSave?: (title: string) => void | Promise<void>;
  /** Дополнительные действия от клиентских модулей (слот расширения) */
  extraActions?: React.ReactNode;
  /** Дополнительные пункты в раскрывающемся меню действий */
  extraMenuItems?: React.ReactNode;
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent':
      return 'red';
    case 'high':
      return 'orange';
    case 'medium':
      return 'blue';
    case 'low':
      return 'gray';
    default:
      return 'blue';
  }
};

const getPriorityLabel = (priority: string) => {
  switch (priority) {
    case 'urgent':
      return 'Срочный';
    case 'high':
      return 'Высокий';
    case 'medium':
      return 'Средний';
    case 'low':
      return 'Низкий';
    default:
      return priority;
  }
};

const getProbabilityColor = (probability: number) => {
  if (probability >= 80) return 'green';
  if (probability >= 50) return 'yellow';
  if (probability >= 20) return 'orange';
  return 'red';
};

/**
 * Заголовок лида: название, статус, приоритет, вероятность, меню действий.
 * Семантика как у DealHeader и PartnerHeader.
 */
export function LeadHeader({
  lead,
  canEdit = false,
  canDelete = false,
  permissionsLoading = false,
  onArchive,
  onUnarchive,
  onEdit,
  onDelete,
  onTitleSave,
  extraActions,
  extraMenuItems,
}: LeadHeaderProps) {
  const archivedPrefix = lead.is_archived ? '[Архивный] ' : '';
  const fallbackTitle = `${archivedPrefix}Лид #${lead.id}`;

  return (
    <div style={{ minWidth: 0, overflow: 'hidden' }}>
      <Group gap='sm' align='center' wrap='nowrap' justify='space-between'>
        {canEdit && onTitleSave && !lead.is_archived ? (
          <Group gap={4} wrap='nowrap' style={{ minWidth: 0, maxWidth: '50%' }}>
            <Text size='lg' fw={600} c='dimmed'>
              #{lead.id}
            </Text>
            {archivedPrefix && (
              <Text size='lg' fw={600} c='dimmed'>
                {archivedPrefix}
              </Text>
            )}
            <EditableText
              value={lead.title ?? ''}
              size='lg'
              canEdit
              placeholder={fallbackTitle}
              onSave={v => void onTitleSave(v)}
              style={{
                fontWeight: 600,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            />
          </Group>
        ) : (
          <Title
            order={3}
            style={{
              fontSize: 18,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              maxWidth: '50%',
            }}
          >
            <Text component='span' c='dimmed' inherit>
              #{lead.id}
            </Text>{' '}
            {archivedPrefix}
            {lead.title || fallbackTitle}
          </Title>
        )}
        <Group gap='sm' align='center' style={{ flexShrink: 0 }}>
          {lead.lead_status && (
            <Badge
              color={lead.lead_status.color || 'gray'}
              variant='light'
              size='sm'
              style={{
                backgroundColor: lead.lead_status.color ? `${lead.lead_status.color}20` : undefined,
                textTransform: 'none',
                fontWeight: 'normal',
              }}
            >
              {lead.lead_status.name}
            </Badge>
          )}
          {lead.priority && (
            <Badge
              color={getPriorityColor(lead.priority)}
              variant='light'
              size='sm'
              style={{ textTransform: 'none', fontWeight: 'normal' }}
            >
              {getPriorityLabel(lead.priority)}
            </Badge>
          )}
          <Badge
            color={getProbabilityColor(lead.probability)}
            variant='light'
            size='sm'
            style={{ textTransform: 'none', fontWeight: 'normal' }}
          >
            {lead.probability}%
          </Badge>
          {extraActions}
          {(canEdit || canDelete || onUnarchive || extraMenuItems) && (
            <Menu withArrow position='bottom-end' withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant='light'
                  size='md'
                  disabled={permissionsLoading}
                  aria-label='Действия'
                >
                  <IconDots size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {extraMenuItems}
                {canEdit && onArchive && (
                  <Menu.Item leftSection={<IconArchive size={14} />} onClick={onArchive}>
                    Архивировать
                  </Menu.Item>
                )}
                {onUnarchive && (
                  <Menu.Item leftSection={<IconArchiveOff size={14} />} onClick={onUnarchive}>
                    Убрать из архива
                  </Menu.Item>
                )}
                {canEdit && onEdit && (
                  <Menu.Item leftSection={<IconEdit size={14} />} onClick={onEdit}>
                    Редактировать
                  </Menu.Item>
                )}
                {canDelete && onDelete && (
                  <Menu.Item leftSection={<IconTrash size={14} />} color='red' onClick={onDelete}>
                    Удалить
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>
    </div>
  );
}
